import { action, set } from '@ember/object';
import { inject as service } from '@ember/service';
import type StoreService from '@ember-data/store';
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import { dropTask } from 'ember-concurrency';
import type AmenityModel from 'garaje/models/amenity';
import type DeskModel from 'garaje/models/desk';
import type MapFeatureModel from 'garaje/models/map-feature';
import type MapFloorModel from 'garaje/models/map-floor';
import type NeighborhoodModel from 'garaje/models/neighborhood';
import type MetricsService from 'garaje/services/metrics';
import type StateService from 'garaje/services/state';
import { DESK_ATTRIBUTES_TO_UPDATE_RESOURCE_OVERVIEW } from 'garaje/utils/enums';
import { defer } from 'rsvp';

interface BulkEditFormArgs {
  neighborhoods: NeighborhoodModel[];
  selectedFeatures: MapFeatureModel[];
  mapFloor: MapFloorModel;
  setSelectedFeatures: (features: MapFeatureModel[]) => void;
  setNeighborhoodToView: (neighborhoodId: string | undefined) => void;
  onClose: () => void;
  updateResourceOverview: (action: string, feature: MapFeatureModel) => void;
}

export default class BulkEditForm extends Component<BulkEditFormArgs> {
  @service declare state: StateService;
  @service declare store: StoreService;
  @service declare metrics: MetricsService;

  @tracked selectedAmenities: AmenityModel[] = [];
  @tracked selectedNeighborhood: NeighborhoodModel[] = [];

  get assignedEmployeeOptions(): string[] {
    return ['Multiple', 'Unassign all'];
  }

  get selectedDesksAreAssigned(): boolean {
    const assignedDesks = this.selectedDesks.filter((desk) => {
      return desk?.assignedTo;
    });
    return assignedDesks.length > 0;
  }

  get assignedEmployeePlaceholder(): string {
    return this.selectedDesksAreAssigned ? 'Multiple' : 'No employees assigned';
  }

  get selectedDesks(): Array<DeskModel | null> {
    return this.args.selectedFeatures.map((feature) => {
      return feature.desk || this.store.peekRecord('desk', feature.externalId);
    });
  }

  get selectedNeighborhoods(): NeighborhoodModel[] {
    const allSelectedNeighborhoods = this.selectedDesks.reduce((acc: NeighborhoodModel[], desk) => {
      const neighborhoodId = desk?.belongsTo('neighborhood').id();
      if (neighborhoodId) {
        const neighborhood = this.store.peekRecord('neighborhood', neighborhoodId);
        if (neighborhood && !acc.includes(neighborhood)) {
          acc.push(neighborhood);
        }
      }
      return acc;
    }, []);
    return allSelectedNeighborhoods;
  }

  // get createNeighborhoodValue(): string {
  //   const isValidNeighborhood = this.findValidNeighborhood(this.neighborhoodSearchValue);
  //   return isValidNeighborhood ? '' : this.neighborhoodSearchValue;
  // }

  @action
  unselectFeature(feature: MapFeatureModel): void {
    const { selectedFeatures, setSelectedFeatures } = this.args;
    const newSelectedFeatures = selectedFeatures.filter((selectedFeature) => selectedFeature !== feature);
    setSelectedFeatures(newSelectedFeatures);
  }

  @action
  onAssignedEmployeeSelect(option: string): void {
    // eslint-disable-next-line no-console
    console.log('onAssignedEmployeeSelect', option);
    // TODO: Add logic for assigned employee dropdown
  }

  @action
  // eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types, @typescript-eslint/no-explicit-any
  editDesk(desk: DeskModel, attr: keyof DeskModel, value: any, feature: MapFeatureModel | null): void {
    const { updateResourceOverview } = this.args;
    if (value === '') {
      set(desk, attr, null);
    } else {
      // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
      set(desk, attr, value);
    }
    if (updateResourceOverview) {
      if (DESK_ATTRIBUTES_TO_UPDATE_RESOURCE_OVERVIEW.includes(attr) && feature) {
        updateResourceOverview('update', feature);
      }
    }
  }

  updateNeighborhoodDeskCount(neighborhood: NeighborhoodModel, count: number): void {
    set(neighborhood, 'numberOfDesks', neighborhood.numberOfDesks + count);
    const currentFloorId = this.args.mapFloor.id;
    if (neighborhood?.deskCountByFloor) {
      set(neighborhood, 'deskCount', neighborhood.deskCount + count);
      let floor = neighborhood.deskCountByFloor.find((floor) => floor['floor-id'] == currentFloorId);
      if (!floor) {
        floor = {
          'floor-id': currentFloorId,
          'desk-count': 0,
        };
      }
      const newDeskCountByFloor = neighborhood.deskCountByFloor.filter((floor) => floor['floor-id'] != currentFloorId);
      floor['desk-count'] += count;
      set(neighborhood, 'deskCountByFloor', [...newDeskCountByFloor, floor]);
    }
  }

  @action
  setSelectedNeighborhood(neighborhood: NeighborhoodModel[]): void {
    this.selectedNeighborhood = neighborhood;
    this.selectedDesks.forEach((desk) => {
      const feature = this.args.selectedFeatures.find((f) => f.externalId === desk?.id || f.desk === desk);
      const prevNeighborhoodId = desk?.belongsTo('neighborhood').id();
      if (prevNeighborhoodId) {
        const prevNeighborhood = this.store.peekRecord('neighborhood', prevNeighborhoodId);
        this.updateNeighborhoodDeskCount(prevNeighborhood!, -1);
      }
      this.editDesk(desk!, 'neighborhood', neighborhood[0], feature!);
      if (neighborhood[0]) {
        this.updateNeighborhoodDeskCount(neighborhood[0], 1);
      }
    });
  }

  @action
  setSelectedAmenities(amenities: AmenityModel[]): void {
    this.selectedAmenities = amenities;
    // Update all selected desks
    // this.editDesk(this.selectedDeskResource, 'amenities', this.selectedAmenities);
  }

  @action
  onNeighborhoodViewClick(): void {
    const neighborhoodId = this.selectedNeighborhoods[0]?.id;
    this.args.setNeighborhoodToView(neighborhoodId);
  }

  // @action
  // onCreateNeighborhood(neighborhood): void {
  //   const { neighborhoods } = this.args;
  // update this to handle bulk changes
  // const prevNeighborhoodId = this.selectedDeskResource.belongsTo('neighborhood').id();
  // if (prevNeighborhoodId) {
  //   const prevNeighborhood = this.store.peekRecord('neighborhood', prevNeighborhoodId);
  //   this.updateNeighborhoodDeskCount(prevNeighborhood, -1);
  // }
  // this.editDesk(this.selectedDeskResource, 'neighborhood', neighborhood);
  // set(neighborhood, 'numberOfDesks', 1);
  // set(neighborhood, 'deskCount', 1);
  // set(neighborhood, 'deskCountByFloor', [
  //   { 'floor-id': this.selectedDeskResource.belongsTo('floor').id(), 'desk-count': 1 },
  // ]);
  // neighborhoods.pushObject(neighborhood);
  // this.clearNeighborhoodErrors();
  // }

  @dropTask
  showCreateNeighborhoodModalTask: {
    perform(): Generator<Promise<unknown>, unknown, unknown>;
  } = {
    *perform(this: { context: BulkEditForm; abort: () => void; continue: () => void }) {
      const deferred = defer();
      this.abort = () => {
        deferred.resolve(false);
      };
      this.continue = () => {
        deferred.resolve(true);
      };
      return yield deferred.promise;
    },
  };
}
